/**
 * @fileoverview User Priority Management Logic
 * Handles user-controlled task priority management functionality
 * Allows users to modify task priorities independently of plan order
 */

import { LogPlanCentroCostoRepository } from '../repositories/LogPlanCentroCostoRepository';

/**
 * Update task priority (user-controlled)
 * @param {string} logId - ID of the log record
 * @param {number} newPriority - New priority value (1-999)
 * @returns {Promise<Object>} Updated log record
 */
export const updateTaskPriority = async (logId, newPriority) => {
  if (!logId) {
    throw new Error('Log ID is required');
  }

  if (typeof newPriority !== 'number') {
    throw new Error('Priority must be a number');
  }

  // Validate priority range
  validatePriorityUpdate(newPriority);

  try {
    const logRepository = new LogPlanCentroCostoRepository();

    // Update task priority
    const updatedLog = await logRepository.updateTaskPriority(logId, newPriority);
    
    return updatedLog;
  } catch (error) {
    console.error('Error updating task priority:', error);
    throw new Error(`Failed to update task priority: ${error.message}`);
  }
};

/**
 * Get tasks grouped by priority for a work order
 * @param {string} ordenId - Work order ID
 * @returns {Promise<Object>} Tasks grouped by priority
 */
export const getTasksByPriority = async (ordenId) => {
  if (!ordenId) {
    throw new Error('Order ID is required');
  }

  try {
    const logRepository = new LogPlanCentroCostoRepository();

    // Get all tasks for the order
    const tasks = await logRepository.getTasksForOrder(ordenId);

    // Group tasks by priority
    const tasksByPriority = {};
    
    tasks.forEach(task => {
      const priority = task.prioridad || null;
      if (!tasksByPriority[priority]) {
        tasksByPriority[priority] = [];
      }
      tasksByPriority[priority].push(task);
    });

    return tasksByPriority;
  } catch (error) {
    console.error('Error getting tasks by priority:', error);
    throw new Error(`Failed to get tasks by priority: ${error.message}`);
  }
};

/**
 * Get tasks by priority range
 * @param {string} ordenId - Work order ID
 * @param {number} minPriority - Minimum priority
 * @param {number} maxPriority - Maximum priority
 * @returns {Promise<Array>} Tasks in priority range
 */
export const getTasksByPriorityRange = async (ordenId, minPriority, maxPriority) => {
  if (!ordenId) {
    throw new Error('Order ID is required');
  }

  if (typeof minPriority !== 'number' || typeof maxPriority !== 'number') {
    throw new Error('Priority range must be numbers');
  }

  if (minPriority > maxPriority) {
    throw new Error('Minimum priority cannot be greater than maximum priority');
  }

  try {
    const logRepository = new LogPlanCentroCostoRepository();

    // Get all tasks for the order
    const tasks = await logRepository.getTasksForOrder(ordenId);

    // Filter tasks by priority range
    const filteredTasks = tasks.filter(task => {
      const priority = task.prioridad;
      return priority && priority >= minPriority && priority <= maxPriority;
    });

    return filteredTasks;
  } catch (error) {
    console.error('Error getting tasks by priority range:', error);
    throw new Error(`Failed to get tasks by priority range: ${error.message}`);
  }
};

/**
 * Validate priority update
 * @param {number} priority - Priority value to validate
 * @throws {Error} If priority is invalid
 */
export const validatePriorityUpdate = (priority) => {
  if (typeof priority !== 'number') {
    throw new Error('Priority must be a number');
  }

  if (priority < 1 || priority > 999) {
    throw new Error('Priority must be between 1 and 999');
  }

  if (!Number.isInteger(priority)) {
    throw new Error('Priority must be an integer');
  }
};

/**
 * Get priority statistics for a work order
 * @param {string} ordenId - Work order ID
 * @returns {Promise<Object>} Priority statistics
 */
export const getPriorityStatistics = async (ordenId) => {
  if (!ordenId) {
    throw new Error('Order ID is required');
  }

  try {
    const tasksByPriority = await getTasksByPriority(ordenId);
    
    const statistics = {
      totalTasks: 0,
      priorityCounts: {},
      averagePriority: 0,
      priorityDistribution: {}
    };

    let totalPriority = 0;
    let tasksWithPriority = 0;

    Object.keys(tasksByPriority).forEach(priority => {
      const taskCount = tasksByPriority[priority].length;
      statistics.totalTasks += taskCount;
      statistics.priorityCounts[priority] = taskCount;
      
      if (priority !== null && priority !== undefined) {
        totalPriority += priority * taskCount;
        tasksWithPriority += taskCount;
      }
    });

    if (tasksWithPriority > 0) {
      statistics.averagePriority = Math.round(totalPriority / tasksWithPriority);
    }

    // Calculate distribution percentages
    Object.keys(statistics.priorityCounts).forEach(priority => {
      const count = statistics.priorityCounts[priority];
      statistics.priorityDistribution[priority] = Math.round((count / statistics.totalTasks) * 100);
    });

    return statistics;
  } catch (error) {
    console.error('Error getting priority statistics:', error);
    throw new Error(`Failed to get priority statistics: ${error.message}`);
  }
};

/**
 * Bulk update priorities for multiple tasks
 * @param {Array} priorityUpdates - Array of {logId, priority} objects
 * @returns {Promise<Array>} Updated log records
 */
export const bulkUpdatePriorities = async (priorityUpdates) => {
  if (!Array.isArray(priorityUpdates)) {
    throw new Error('Priority updates must be an array');
  }

  if (priorityUpdates.length === 0) {
    return [];
  }

  try {
    const results = [];
    
    // Process updates sequentially to maintain data consistency
    for (const update of priorityUpdates) {
      if (!update.logId || typeof update.priority !== 'number') {
        throw new Error('Each update must have logId and priority');
      }

      const result = await updateTaskPriority(update.logId, update.priority);
      results.push(result);
    }

    return results;
  } catch (error) {
    console.error('Error in bulk priority update:', error);
    throw new Error(`Failed to bulk update priorities: ${error.message}`);
  }
};

/**
 * Reset all task priorities to default (based on orden_plan)
 * @param {string} ordenId - Work order ID
 * @returns {Promise<Array>} Updated log records
 */
export const resetPrioritiesToDefault = async (ordenId) => {
  if (!ordenId) {
    throw new Error('Order ID is required');
  }

  try {
    const logRepository = new LogPlanCentroCostoRepository();

    // Get all tasks for the order
    const tasks = await logRepository.getTasksForOrder(ordenId);

    // Sort by orden_plan to get correct sequence
    const sortedTasks = tasks.sort((a, b) => (a.orden_plan || 0) - (b.orden_plan || 0));

    // Update priorities to match orden_plan sequence
    const priorityUpdates = sortedTasks.map((task, index) => ({
      logId: task.id,
      priority: index + 1
    }));

    const results = await bulkUpdatePriorities(priorityUpdates);

    return results;
  } catch (error) {
    console.error('Error resetting priorities to default:', error);
    throw new Error(`Failed to reset priorities to default: ${error.message}`);
  }
};












